<template>
  <article class="balance-forecast">
    <div class="balance-forecast__head">
      <div class="balance-forecast__head-text">
        <h3 class="balance-forecast__title">
          {{ title }}
        </h3>

        <div class="balance-forecast__note">
          {{ note }}
        </div>
      </div>

      <v-btn
        v-if="promisedShown && canUseBilling"
        class="balance-forecast__promised"
        x-large
        outlined
        :loading="activating"
        @click="$emit('activate-promised')"
      >
        <v-icon left size="16">
          mdi-clock-outline
        </v-icon>
        Обещанный платеж
      </v-btn>

      <span v-else-if="promisedPending" class="balance-forecast__mark">
        <v-icon size="16">
          mdi-clock-outline
        </v-icon>
        Обещанный платёж включён
      </span>
    </div>

    <template v-if="daysLeft !== null || promisedDaysLeft !== null">
      <div class="balance-forecast__track">
        <div
          :class="['balance-forecast__fill', `balance-forecast__fill_${level}`]"
          :style="{ width: `${shownPercent}%` }"
        />
        <div
          v-if="promisedPending"
          class="balance-forecast__fill balance-forecast__fill_promised"
          :style="{ width: `${shownPromisedPercent}%` }"
        />
      </div>

      <div class="balance-forecast__scale">
        <span class="balance-forecast__scale-start">сегодня</span>
        <span v-if="scaleEndLabel" class="balance-forecast__scale-end">{{ scaleEndLabel }}</span>
      </div>
    </template>
  </article>
</template>

<script>
  import { DateTime } from 'luxon'
  import { formatIsoDateTime } from '@/util/date-time-helper'
  import { daysLabel } from '@/util/plural'

  export default {
    name: 'BalanceForecastCard',
    props: {
      daysLeft: {
        type: Number,
        default: null,
      },
      validUntil: {
        type: String,
        default: null,
      },
      expiringSoon: {
        type: Boolean,
        default: false,
      },
      // Пороги из настроек бэка: с attention шкала желтеет, с warning краснеет
      warningDays: {
        type: Number,
        default: null,
      },
      attentionDays: {
        type: Number,
        default: null,
      },
      promisedPayment: {
        type: Object,
        default: null,
      },
      canManage: {
        type: Boolean,
        default: false,
      },
      activating: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        // Стартуем с пустой шкалы: заполнение проигрывается при появлении карточки
        shownPercent: 0,
        shownPromisedPercent: 0,
      }
    },
    computed: {
      // Полная шкала — текущий календарный месяц: столько покрывает одно пополнение по тарифу
      scaleDays () {
        return DateTime.now().daysInMonth
      },
      title () {
        if (this.promisedDaysLeft !== null) {
          return this.promisedDaysLeft === 0
            ? 'Обещанный платеж заканчивается сегодня'
            : `Обещанный платеж действует ещё ${daysLabel(this.promisedDaysLeft)}`
        }
        if (this.daysLeft === null) return 'Списаний по тарифу нет'
        if (this.daysLeft === 0) return 'Баланса не хватает на день по тарифу'
        return `Баланса хватит на ${daysLabel(this.daysLeft)}`
      },
      note () {
        if (this.promisedDaysLeft !== null) {
          return `Списание за тариф идёт каждый день. Доступ по отсрочке до ${this.promisedDueLabel}`
        }
        if (this.daysLeft === null) return 'Тариф не назначен или в нём нет активных филиалов'
        if (!this.validUntil) return 'Списание за тариф идёт каждый день. Пополните баланс, чтобы сохранить доступ'
        if (this.promisedPending) {
          return `Доступ активен до ${this.validUntilLabel}. Обещанный платёж включён: когда баланс закончится, доступ сохранится ещё на ${daysLabel(this.promisedPayment.termDays)} — до ${this.promisedEndLabel}`
        }
        return `Списание за тариф идёт каждый день. Доступ активен до ${this.validUntilLabel}`
      },
      // Остаток дней отсрочки считает бэк: на отсрочке баланс в минусе, и daysLeft
      // от тарифа всегда 0 — шкале нечего было бы показывать
      promisedDaysLeft () {
        if (!this.promisedActive) return null

        const left = this.promisedPayment.daysLeft

        return typeof left === 'number' ? Math.max(0, left) : null
      },
      // Полная шкала отсрочки — день выдачи плюс её срок, поэтому знаменатель на день больше
      promisedTermDays () {
        return this.promisedPayment && this.promisedPayment.termDays > 0
          ? this.promisedPayment.termDays
          : this.scaleDays
      },
      promisedDueLabel () {
        return formatIsoDateTime(this.promisedPayment.dueAt, 'd MMMM', '—', null, 'ru')
      },
      fillPercent () {
        // Сегодняшний день ещё не прожит, поэтому считаем его: иначе в последний
        // день отсрочки полоса была бы пустой, хотя доступ ещё есть
        if (this.promisedDaysLeft !== null) {
          return Math.min((this.promisedDaysLeft + 1) / (this.promisedTermDays + 1), 1) * 100
        }

        return Math.min(this.daysLeft / this.scaleDays, 1) * 100
      },
      // Полоска отсрочки идёт следом за полоской баланса и на той же шкале:
      // столько дней доступ продержится сверх оплаченных
      promisedPercent () {
        if (!this.promisedPending) return 0

        const rest = Math.max(this.scaleDays - this.daysLeft, 0)

        return Math.min(this.promisedPayment.termDays, rest) / this.scaleDays * 100
      },
      // Красная — доступ вот-вот закроется или уже живём на отсрочке;
      // оранжевая — мягкий порог, до предупреждения ещё есть время
      level () {
        if (this.promisedActive) return 'alarm'
        if (this.daysLeft === null) return 'normal'

        const alarm = this.warningDays === null
          ? this.expiringSoon
          : this.daysLeft <= this.warningDays

        if (alarm) return 'alarm'
        if (this.attentionDays !== null && this.daysLeft <= this.attentionDays) return 'warning'

        return 'normal'
      },
      promisedActive () {
        return !!this.promisedPayment && this.promisedPayment.active
      },
      // Включён заранее, срок ещё не пошёл: баланс тратится как обычно
      promisedPending () {
        return !!this.promisedPayment && this.promisedPayment.pending && this.daysLeft !== null
      },
      // Доступность целиком считает бэк: рубильник, окно по остатку дней, лимит месяца
      promisedShown () {
        return this.canManage && !!this.promisedPayment && this.promisedPayment.available
      },
      validUntilLabel () {
        return formatIsoDateTime(this.validUntil, 'd MMMM yyyy', '—', null, 'ru')
      },
      validUntilShort () {
        return formatIsoDateTime(this.validUntil, 'd MMMM', '—', null, 'ru')
      },
      // Ориентир конца отсрочки: точную дату бэк назначит в день применения,
      // поэтому рядом оставляем и срок
      promisedEndLabel () {
        const end = DateTime.fromISO(this.validUntil).plus({ days: this.promisedPayment.termDays })

        return end.isValid ? end.setLocale('ru').toFormat('d MMMM') : '—'
      },
      scaleEndLabel () {
        if (this.promisedDaysLeft !== null) return this.promisedDueLabel
        if (!this.validUntil) return null
        if (this.promisedPending) {
          return `${this.validUntilShort} + ${daysLabel(this.promisedPayment.termDays)} (${this.promisedEndLabel})`
        }

        return this.validUntilShort
      },
    },
    watch: {
      fillPercent: 'playFill',
      promisedPercent: 'playFill',
    },
    mounted () {
      this.playFill()
    },
    methods: {
      // Ширину ставим следующим кадром — иначе браузер отрисует конечное значение сразу, без перехода
      playFill () {
        this.$nextTick(() => {
          window.requestAnimationFrame(() => {
            this.shownPercent = this.fillPercent
            this.shownPromisedPercent = this.promisedPercent
          })
        })
      },
    },
  }
</script>

<style lang="less" scoped>
// Цвет отсрочки из макета: у обещанного платежа своя роль — ни primary, ни error
@color-promised: #eb316c;
// Столько же идёт заполнение шкалы в макете (шейдер progress-fill, u_duration)
@fill-duration: 1.4s;

.balance-forecast {
  background-color: #fff;
  border: 1px solid #c4cdd9;
  border-radius: 5px;
  padding: 24px;

  .media-xs({
    padding: 20px 16px;
  });

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  &__head-text {
    min-width: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #323030;
  }

  &__note {
    margin-top: 6px;
    font-size: 13px;
    color: #757575;
  }

  &__promised.v-btn {
    flex: 0 0 auto;
    height: 28px;
    border: 1px solid @color-promised;
    border-radius: 3px;
    color: @color-promised;
    font-size: 11px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: normal;

    // По макету заливка появляется только на наведении
    &::before {
      background-color: @color-promised;
    }
  }

  &__mark {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    height: 28px;
    padding: 0 10px;
    border: 1px solid @color-promised;
    border-radius: 3px;
    color: @color-promised;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;

    .v-icon {
      color: inherit;
    }
  }

  &__track {
    display: flex;
    margin-top: 18px;
    height: 10px;
    border-radius: 5px;
    background-color: @color-secondary;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    border-radius: 5px;
    background-color: @color-success;
    transition: width @fill-duration cubic-bezier(0.25, 0.8, 0.25, 1), background-color 0.3s ease;

    &_warning {
      background-color: @color-primary;
    }

    &_alarm {
      background-color: @color-promised;
    }

    // Отсрочка ещё не применена — штриховка отличает её от красной полоски
    // баланса, которая при малом остатке того же цвета
    &_promised {
      flex: 0 0 auto;
      margin-left: 2px;
      background-color: @color-promised;
      background-image: repeating-linear-gradient(
        -45deg,
        transparent 0,
        transparent 4px,
        rgba(255, 255, 255, 0.45) 4px,
        rgba(255, 255, 255, 0.45) 8px
      );
    }
  }

  &__scale {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    margin-top: 8px;

    &-start {
      font-size: 10px;
      color: #999;
    }

    &-end {
      font-size: 11px;
      font-weight: 700;
      color: #ea306b;
    }
  }
}
</style>
